import { NgModule, ErrorHandler, provideZoneChangeDetection, provideAppInitializer, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { StateService, trace, TransitionService } from '@uirouter/angular';

import { TranslateLoader, TranslateModule, TranslateParser, TranslateService } from '@ngx-translate/core';

import { SessionExtendHttpInterceptorProvider } from 'rev-shared/security/SessionExtendHttpInterceptor.Provider';
import { StateTransitionScrollToTop } from 'rev-shared/config/StateTransitionScrollToTop.Run';
import { configureStateTransitionLogging } from 'rev-shared/config/ConfigureStateTransitionLogging.Config';

import { HttpParamsInterceptorProvider } from './HttpParamsInterceptor.Provider';
import { LocaleProvider } from './Locale.Provider';
import { VbTranslateParser } from './VbTranslateParser';
import { VbXsrfCookieExtractorProvider } from './VbXsrfCookieExtractor.Provider';
import { createTranslateLoader } from './TranslateHttpLoader.Provider';
import { locale$ } from './LocaleContext';

import { ExceptionHandler } from '../config/ExceptionHandler.config';

//Import on lazy loaded root modules
@NgModule({
	providers: [
		provideHttpClient(withInterceptorsFromDi()),
		HttpParamsInterceptorProvider,
		SessionExtendHttpInterceptorProvider,
		VbXsrfCookieExtractorProvider, // faciliates our dyanmic cookie naming
	]
})
export class AngularRootModuleDeps {}

//import on app root module
@NgModule({
	imports: [
		AngularRootModuleDeps,
		BrowserAnimationsModule,
		BrowserModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: createTranslateLoader,
				deps: [
					HttpClient
				]
			},
			parser: {
				provide: TranslateParser,
				useClass: VbTranslateParser
			},
			useDefaultLang: false
		})
	],
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		LocaleProvider,
		provideAppInitializer(() => {
			const transitions = inject(TransitionService);
			const stateService = inject(StateService);
			StateTransitionScrollToTop(transitions);
			configureStateTransitionLogging(stateService, trace, transitions);
			const translateService = inject(TranslateService);
			return firstValueFrom(locale$)
				.then(({ language }) => firstValueFrom(translateService.use(language)))
				.catch(err => console.error('Error loading translation file:', err));
		}),
		{ provide: ErrorHandler, useClass: ExceptionHandler }
	]
})
export class AngularAppBootstrapDepsModule {}
